import { useGateValue } from '@coursera-web/statsig';

import { breakpoints, useMediaQuery } from '@coursera/cds-core';

import { useThirdPartyOrganization } from 'bundles/enterprise-admin/ThirdPartyOrganizationProvider';
import { IdTypes, createFeatureScope } from 'bundles/product-features';

const { useProductFeatures } = createFeatureScope({
  enterprise: {
    enableProgramAutoJoin: false,
    enablePersonalizedReordering: false,
    enableAdminNavRedesign: false,
    enableAdminCoachV2: false,
    enableNativeAnalyticsDashboards: false,
    enableMessagingV2: false,
  },
});

/**
 * Hook to check if the Program Auto-Join feature is enabled for an organization.
 * When enabled, learners are automatically placed into programs and the removal
 * flow uses simplified "permanent removal" / "re-entry allowed" options.
 *
 * @param organizationId - The organization ID to check the feature flag for.
 * @returns Whether `enableProgramAutoJoin` is enabled for the given organization.
 */
export const useEnableProgramAutoJoin = (organizationId: string): boolean => {
  const { features } = useProductFeatures({
    idType: IdTypes.Organization,
    id: organizationId,
    skip: !organizationId,
  });
  return features.get('enterprise', 'enableProgramAutoJoin');
};

/**
 * Hook to check if Personalized Reordering is enabled for an organization.
 *
 * @param organizationId - The organization ID to check the feature flag for.
 * @returns Whether `enablePersonalizedReordering` is enabled for the given organization.
 */
export const useEnablePersonalizedReordering = (organizationId: string): boolean => {
  const { features } = useProductFeatures({
    idType: IdTypes.Organization,
    id: organizationId,
    skip: !organizationId,
  });
  return features.get('enterprise', 'enablePersonalizedReordering');
};

/**
 * Returns whether the admin side nav is active.
 *
 * The admin side nav is active if the admin nav redesign feature flag is enabled
 * and the viewport is not mobile.
 *
 * @returns Whether the admin side nav is active.
 */
export const useIsAdminSideNavActive = (): boolean => {
  const isNotMobile = useMediaQuery(breakpoints.up('sm'));
  const org = useThirdPartyOrganization();

  const { features } = useProductFeatures({
    idType: IdTypes.Organization,
    id: org?.id,
    skip: !org?.id,
  });

  const isAdminNavRedesignEnabled = features.get('enterprise', 'enableAdminNavRedesign');

  return isAdminNavRedesignEnabled && isNotMobile;
};

/**
 * Hook to check if Messaging V2 is enabled for an organization.
 * When enabled, the admin Messages nav points to the new `/admin/messaging`
 * route instead of the legacy automated-messages list.
 *
 * @param organizationId - The organization ID to check the feature flag for.
 * @returns Whether `enableMessagingV2` is enabled for the given organization.
 */
export const useEnableMessagingV2 = (organizationId: string): boolean => {
  const { features } = useProductFeatures({
    idType: IdTypes.Organization,
    id: organizationId,
    skip: !organizationId,
  });
  return features.get('enterprise', 'enableMessagingV2');
};

/**
 * Hook to check if AdminCoach V2 is enabled for an organization.
 *
 * @returns Whether `enableAdminCoachV2` is enabled for the current organization.
 */
export const useIsAdminCoachV2Enabled = (): boolean => {
  const org = useThirdPartyOrganization();

  const { features } = useProductFeatures({
    idType: IdTypes.Organization,
    id: org?.id ?? '',
    skip: !org?.id,
    ssr: true,
  });

  return features.get('enterprise', 'enableAdminCoachV2');
};

export const useEnableNativeAnalytics = (): boolean => {
  const org = useThirdPartyOrganization();
  const { features } = useProductFeatures({
    idType: IdTypes.Organization,
    id: org?.id ?? '',
    skip: !org?.id,
    ssr: true,
  });

  // Adding a gate so internal users can test for prod orgs with real data.
  // TODO: Remove this once testing is complete
  const enableForProdOrgsForTesting = useGateValue('native-analytics-dashboard');
  return features.get('enterprise', 'enableNativeAnalyticsDashboards') || enableForProdOrgsForTesting;
};
